import {
  collection,
  addDoc,
  getDocs,
  doc,
  writeBatch,
  serverTimestamp,
  query,
  orderBy,
  limit,
  where
} from "firebase/firestore";
import { db } from "../../../firebase/firestore.js";
import { auth } from "../../../firebase/auth.js";
import { MOVEMENT_TYPES } from "../models/inventory.js";

const COLLECTION_NAME = "inventoryMovements";
const ITEMS_COLLECTION = "items";

/**
 * Record a stock adjustment using a Firestore writeBatch
 * Updates items/{itemId}.stock AND creates inventoryMovements/{id} atomically.
 */
export async function recordStockAdjustment({
  itemId,
  itemName,
  itemSku,
  currentStock,
  adjustmentQty,
  reason,
  notes = ""
}) {
  const qty = Number(adjustmentQty);
  const prev = Number(currentStock);
  const next = prev + qty;

  const movementType = qty > 0 ? MOVEMENT_TYPES.IN : (qty < 0 ? MOVEMENT_TYPES.OUT : MOVEMENT_TYPES.ADJUSTMENT);
  const user = auth.currentUser?.email || auth.currentUser?.uid || "admin";

  const batch = writeBatch(db);

  // 1. Update item stock
  const itemRef = doc(db, ITEMS_COLLECTION, itemId);
  batch.update(itemRef, {
    stock: next,
    updatedAt: serverTimestamp()
  });

  // 2. Create audit movement record
  const movementRef = doc(collection(db, COLLECTION_NAME));
  batch.set(movementRef, {
    itemId,
    itemName: itemName || "",
    itemSku: itemSku || "",
    type: movementType,
    quantity: Math.abs(qty),
    previousStock: prev,
    newStock: next,
    reason: reason || "Physical Audit",
    notes: notes || "",
    referenceId: null,
    createdBy: user,
    createdAt: serverTimestamp()
  });

  await batch.commit();
  return { id: movementRef.id, newStock: next };
}

/**
 * Fetch recent inventory movements across all items
 */
export async function getRecentMovements(limitCount = 30) {
  const movementsRef = collection(db, COLLECTION_NAME);
  try {
    const q = query(movementsRef, orderBy("createdAt", "desc"), limit(limitCount));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({
      id: d.id,
      ...d.data(),
      createdAt: d.data().createdAt?.toDate ? d.data().createdAt.toDate().toISOString() : d.data().createdAt
    }));
  } catch (_) {
    // Fallback if index is not ready yet
    const snapshot = await getDocs(movementsRef);
    return snapshot.docs.map((d) => ({
      id: d.id,
      ...d.data(),
      createdAt: d.data().createdAt?.toDate ? d.data().createdAt.toDate().toISOString() : d.data().createdAt
    }));
  }
}

/**
 * Fetch movement history for a specific item
 */
export async function getItemMovements(itemId) {
  const movementsRef = collection(db, COLLECTION_NAME);
  try {
    const q = query(
      movementsRef,
      where("itemId", "==", itemId),
      orderBy("createdAt", "desc")
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({
      id: d.id,
      ...d.data(),
      createdAt: d.data().createdAt?.toDate ? d.data().createdAt.toDate().toISOString() : d.data().createdAt
    }));
  } catch (_) {
    const snapshot = await getDocs(movementsRef);
    return snapshot.docs
      .filter((d) => d.data().itemId === itemId)
      .map((d) => ({
        id: d.id,
        ...d.data(),
        createdAt: d.data().createdAt?.toDate ? d.data().createdAt.toDate().toISOString() : d.data().createdAt
      }));
  }
}
